/**
 * Tour de France 2026 – Bingo Card
 *
 * Renders the final 3x3 bingo card as HTML (8 random items for the chosen
 * Fan/Hero side + a center logo cell). Item names are shown; descriptions are
 * kept in the markup for the (designer-supplied) click-to-reveal interaction.
 *
 * Download / copy / share snapshot the rendered card via html2canvas.
 */

/* global snapdom */
/* eslint-disable no-unused-vars */

var TDF2026 = (function () {
    'use strict';

    function TDF2026() {
        var data = (window.ys && window.ys.tdf2026) || {};
        this.cardEl   = document.getElementById('tdf26-bingo-card');
        this.items    = data.bingoItems || [];
        this.shareUrl = data.shareUrl || window.location.href;
        this.usertype = null;
    }

    function escapeHtml(s) {
        return String(s == null ? '' : s)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');
    }

    function shuffle(arr) {
        return arr.slice().sort(function () { return 0.5 - Math.random(); });
    }

    // -------------------------------------------------------------------------
    // Rendering
    // -------------------------------------------------------------------------

    /**
     * Render the card for a given side ("Fan" / "Hero").
     */
    TDF2026.prototype.renderCard = function (usertype) {
        this.usertype = usertype;
        if (!this.cardEl) { return; }

        var pool = this.items.filter(function (it) {
            return !usertype || String(it.type).toLowerCase() === String(usertype).toLowerCase();
        });

        if (pool.length < 8) {
            console.warn('[TDF2026] Only ' + pool.length + ' bingo items available for "' + usertype + '" (need 8).');
        }

        var picked = shuffle(pool).slice(0, 8);
        // 3x3 grid: 4 items, center logo, 4 items.
        var cells = picked.slice(0, 4).concat([null]).concat(picked.slice(4, 8));

        var html = this.cardEl.innerHTML;
        cells.forEach(function (cell) {
            if (cell === null) {
                html += '<div class="tdf26-bingo-cell tdf26-bingo-cell--logo"></div>';
                return;
            }
            html += '<div class="tdf26-bingo-cell" data-description="' + escapeHtml(cell.description) + '">' +
                        '<span class="tdf26-bingo-cell__name">' + escapeHtml(cell.name) + '</span>' +
                        '<span class="tdf26-bingo-cell__desc">' + escapeHtml(cell.description) + '</span>' +
                    '</div>';
        });

        this.cardEl.innerHTML = html;
    };

    // -------------------------------------------------------------------------
    // Image capture (html2canvas)
    // -------------------------------------------------------------------------

    TDF2026.prototype._toBlob = function (cb) {
        var node = this.cardEl;
        if (!node || typeof snapdom === 'undefined') {
            console.warn('[TDF2026] snapdom unavailable.');
            cb(null);
            return;
        }

        // The card is absolutely positioned + vertically centered (top:50% /
        // translateY(-50%)) and may be zoomed on mobile. Neutralise those during
        // capture only, so the snapshot is the card's own box at full size.
        var s    = node.style;
        var prev = { top: s.top, transform: s.transform, zoom: s.zoom };
        s.top       = '0';
        s.transform = 'none';
        s.zoom      = '1';

        function restore() {
            s.top       = prev.top;
            s.transform = prev.transform;
            s.zoom      = prev.zoom;
        }

        snapdom(node, { scale: 2, backgroundColor: null })
            .then(function (result) { return result.toBlob({ type: 'png' }); })
            .then(function (blob) { restore(); cb(blob); })
            .catch(function (err) {
                restore();
                console.warn('[TDF2026] snapdom failed:', err);
                cb(null);
            });
    };

    

    TDF2026.prototype.downloadCard = function () {
        this._toBlob(function (blob) {
            if (!blob) { return; }
            var url = URL.createObjectURL(blob);
            var a = document.createElement('a');
            a.href = url;
            a.download = 'tdf-2026-bingo-card.png';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        });
    };

    TDF2026.prototype.copyCard = function () {
        var self = this;

        // Image clipboard needs a secure context (HTTPS / localhost) + ClipboardItem.
        if (navigator.clipboard && typeof window.ClipboardItem !== 'undefined') {
            this._toBlob(function (blob) {
                if (!blob) { self._copyLink(); return; }
                navigator.clipboard.write([new window.ClipboardItem({ 'image/png': blob })])
                    .catch(function (e) {
                        console.warn('[TDF2026] image copy failed, copying link instead:', e);
                        self._copyLink();
                    });
            });
            return;
        }

        // No image clipboard (e.g. non-HTTPS dev) → copy the page link instead.
        console.warn('[TDF2026] Image clipboard unavailable (needs HTTPS); copying link.');
        this._copyLink();
    };

    /**
     * Copy the share URL to the clipboard, with a legacy fallback that also
     * works in insecure contexts.
     */
    TDF2026.prototype._copyLink = function () {
        var text = this.shareUrl;

        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(text).catch(function () {});
            return;
        }

        var ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        try { document.execCommand('copy'); } catch (e) { /* noop */ }
        document.body.removeChild(ta);
    };

    /**
     * Native share with the card image (mobile); falls back to download.
     */
    TDF2026.prototype.shareCard = function () {
        var self = this;
        this._toBlob(function (blob) {
            if (blob && navigator.share) {
                var file = new File([blob], 'tdf-2026-bingo-card.png', { type: 'image/png' });
                if (!navigator.canShare || navigator.canShare({ files: [file] })) {
                    navigator.share({ title: 'Tour de France 2026', files: [file] })
                        .catch(function (err) {
                            if (err && err.name !== 'AbortError') { self.downloadCard(); }
                        });
                    return;
                }
            }
            self.downloadCard();
        });
    };

    /**
     * Open a social share intent for the page URL.
     */
    TDF2026.prototype.shareTo = function (network) {
        var url  = encodeURIComponent(this.shareUrl);
        var text = encodeURIComponent('My Epiq Bingo Card – Tour de France 2026');
        var dest = '';

        switch (network) {
            case 'facebook': dest = 'https://www.facebook.com/sharer/sharer.php?u=' + url; break;
            case 'x':        dest = 'https://twitter.com/intent/tweet?url=' + url + '&text=' + text; break;
            case 'whatsapp': dest = 'https://wa.me/?text=' + text + '%20' + url; break;
            case 'mail':     dest = 'mailto:?subject=' + text + '&body=' + text + '%20' + url; break;
        }

        if (dest) { window.open(dest, '_blank', 'noopener'); }
    };

    return TDF2026;
})();
